import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from '@americanexpress/dls-react';
import { useSelector } from 'react-redux';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import {
  useCommonContent,
  useOneCmsLocalePartners,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import LoungeTermsContent from '@/components/common/LoungeTermsContent';
import {
  useLoungeAccessListMatchedWithLocaleProviders,
  useLoungePrioritisedAccessRules,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import {
  enrollmentStatuses,
  usePriorityPassEnrollmentDetails,
} from '@/hooks/priorityPassHooks/priorityPassHelperHooks';
import { useRouteParams } from '@/context/routeParamsContext';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getMycaApiUrl } from '@/selectors/mycaSelectors';
import { useModal } from '@/context/modalContext';
import {
  splitAccessAndGuestPolicyHeader,
  isPriorityPassLounge,
  formatText,
} from '@/helpers/utils';
import { Card } from '@/constants';
import LoginCard from '../LoginCard';

const AccessPolicyModal = () => {
  const { loungeId, product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const mycaApiUrl = useSelector(getMycaApiUrl);
  const { closeModal } = useModal();

  const {
    lounges: { agTab, accessModal },
    priorityPass: { header },
    login: { label },
    footer: { additionalInfo },
  } = useCommonContent();
  const partners = useOneCmsLocalePartners();
  const [accessPolicyHeaderText] = splitAccessAndGuestPolicyHeader(agTab);
  const { accessLevel } =
    useLoungeAccessListMatchedWithLocaleProviders(loungeId);
  const id = 'access-policy';
  const isLoggedIn = useSelector(getIsLoggedIn);
  const accessRules = useLoungePrioritisedAccessRules(loungeId);

  const {
    enabled: isPriorityPassEnabled,
    isStatusEnabledMarket,
    status: enrollmentStatus,
    badge: priorityPassBadgeStatus,
    content: priorityPassContent,
  } = usePriorityPassEnrollmentDetails();
  const isPriorityPass =
    isPriorityPassEnabled && isPriorityPassLounge(accessRules);
  const userProduct = useSelectedProductFromUserWallet(product, locale);
  const lfProduct = userProduct?.loungeFinderProduct;
  const loungeCollection =
    lfProduct === Card.Centurion
      ? 'global-lounge-collection'
      : 'the-lounge-collection';
  const type = lfProduct === Card.Centurion ? 'centurion' : 'platinum';
  const enrollmentUrl = `${mycaApiUrl}/card-benefits/enroll/${loungeCollection}/${type}`;

  const description =
    enrollmentStatus === enrollmentStatuses.NOT_ENROLLED &&
    isLoggedIn &&
    userProduct?.hasLoungeBenefit &&
    isStatusEnabledMarket &&
    !product?.includes('idc')
      ? formatText(priorityPassContent?.description_loggedIn, {
          url: enrollmentUrl,
        })
      : priorityPassContent?.description;

  return (
    <Modal
      id={id}
      onClose={(e) => closeModal(e, 'close access policy')}
      innerContainerClassName="tw-modal-inner-container"
    >
      <ModalHeader
        id={`${id}-header`}
        closeButtonProps={{ reactlytics: { disable: true } }}
      >
        <h3>{accessPolicyHeaderText}</h3>
      </ModalHeader>
      <ModalBody id={`${id}-content`} className="tw-modal-body">
        <div>
          {!isLoggedIn && <LoginCard label={label} />}
          <h4 className="tw-heading-xs-bold">{accessModal?.policyHeader}</h4>
          <div className="tw-pt-2">{accessLevel}</div>
        </div>
        {isPriorityPass && (
          <>
            <hr
              className="tw-my-6 tw-border-gray-300"
              aria-hidden="true"
              style={{ width: 'unset' }}
            />
            <div>
              <h4 className="tw-heading-xs-bold">{header}</h4>
              <Badge
                className="tw-body-xs-bold tw-py-2"
                status={priorityPassBadgeStatus}
                size="large"
                type="text"
              >
                {priorityPassContent?.title}
              </Badge>
              <p
                className="a-children_tw-text-blue-bright-600 a-children_tw-underline"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </>
        )}
        <hr
          className="tw-my-6 tw-border-gray-300"
          aria-hidden="true"
          style={{ width: 'unset' }}
        />
        <h4 className="tw-heading-xs-bold tw-pb-2 tw-text-sm">
          {additionalInfo?.termsLabel}
        </h4>
        <LoungeTermsContent
          loungeId={loungeId}
          partners={partners}
          textCustomStyles="tw-text-gray-700"
        />
      </ModalBody>
    </Modal>
  );
};

export default AccessPolicyModal;
